import {useState, useEffect} from 'react';
import {
    AppShell, Burger, Button, Group, Title, Text,
    Divider, ScrollArea, Skeleton, Tabs, Image, Badge, Stack
} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {AdminWinnersTab} from "./admin-page-components/AdminWinnersTab";
import {AdminParticipantsTab} from "./admin-page-components/AdminParticipantsTab";
import {AdminRewardsTab} from "./admin-page-components/AdminRewardsTab";
import api from '../api';
import {useAuth} from "../hooks/useAuth";
import CreateCampaignModal from './admin-page-components/CreateCampaignModal';


interface Campaign {
    id: number;
    pid: string;
    name: string;
    status: string;
    startDate: string;
    endDate: string;
    participants: number;
    participantsToday: number;
}

export function AdminPage() {
    const [opened, {toggle}] = useDisclosure();
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
    const [newCampaignOpened, setNewCampaignOpened] = useState(false);
    const {logout} = useAuth();


    useEffect(() => {
        const fetchCampaigns = async () => {
            try {
                const response = await api.get(`/admin/campaigns`);

                const data = response.data;

                const campaignData = data.content.map((item: any, index: number) => ({
                    id: index,
                    pid: item.campaignPid,
                    name: item.name,
                    status: new Date(item.endDate) > new Date() ? 'active' : 'inactive',
                    startDate: '',
                    endDate: item.endDate,
                    participants: 0,
                    participantsToday: 0,
                }));

                setCampaigns(campaignData);
            } catch (error) {
                console.error('Error loading campaigns:', error);
            }
        };

        fetchCampaigns();
    }, []);

    const handleCampaignSelect = async (campaign: Campaign) => {
        setSelectedCampaign(campaign);

        try {
            const response = await api.get(`/admin/campaigns/${campaign.pid}`);
            const data = response.data;

            setSelectedCampaign(prev => ({
                ...prev!,
                startDate: data.startDate || prev!.startDate,
                endDate: data.endDate || prev!.endDate,
                participants: data.participantCount ?? prev!.participants,
                participantsToday: data.participantsCountToday ?? prev!.participantsToday,
                status: data.status === 'ACTIVE'
                    ? 'active'
                    : data.status === 'FINISHED' || data.status === 'CONCLUDED'
                        ? 'inactive'
                        : data.status.toLowerCase(),
            }));
        } catch (error) {
            console.error('Error loading campaign details:', error);
        }
    };

    return (
        <AppShell
            header={{height: 80}}
            navbar={{width: 300, breakpoint: 'sm', collapsed: {mobile: !opened}}}
            padding="md"
        >
            <AppShell.Header>
                <Group justify="space-between" className=" py-2 px-5 bg-green-100 h-full">
                    <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm"/>
                    <div className="h-full flex justify-center">
                        <div className="h-full">
                            <Image src="/assets/promeks_logo.png?v=2" className="h-full w-auto object-contain"
                                   alt="Promeks Logo"/>
                        </div>
                        <Stack className="ml-5" justify="center" gap="xs">
                            <Text fs="italic" size="sm">Zdravo,</Text>
                            <Text>IME KLIJENTA !</Text>
                        </Stack>
                    </div>


                    <Button onClick={logout}>Logout</Button>
                </Group>
            </AppShell.Header>

            <AppShell.Navbar p="md">
                <Title order={3} mb="lg">Kampanje</Title>
                <Divider my="lg"/>

                <ScrollArea h="calc(100vh - 260px)" offsetScrollbars>
                    <Stack gap="sm">
                        {campaigns.map((campaign) => (
                            <div
                                key={campaign.id}
                                onClick={() => handleCampaignSelect(campaign)}
                                className={`p-4 rounded-xl border transition-all cursor-pointer
            ${selectedCampaign?.id === campaign.id
                                    ? 'bg-teal-50 border-teal-300 shadow-md'
                                    : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm'
                                }`}
                            >
                                <Group justify="apart" align="center" mb="xs">
                                    <Title order={5} className="line-clamp-2">
                                        {campaign.name}
                                    </Title>

                                    <Badge
                                        color={campaign.status === 'active' ? 'green' : 'red'}
                                        variant="light"
                                        size="sm"
                                    >
                                        {campaign.status === 'active' ? 'Aktivna' : 'Neaktivna'}
                                    </Badge>
                                </Group>

                                <Group gap="xs" align="center">
                                    <div
                                        className={`w-3 h-3 rounded-full ${
                                            campaign.status === 'active' ? 'bg-green-500' : 'bg-red-500'
                                        }`}
                                    />
                                    <Text size="sm" c="dimmed">
                                        {campaign.status === 'active' ? 'Aktivna' : 'Završena'}
                                    </Text>
                                </Group>

                                <Text size="sm" mt="xs" c="dimmed">
                                    <span className="font-medium text-gray-700">KRAJ:</span>{' '}
                                    {new Date(campaign.endDate).toLocaleDateString('sr-RS', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric',
                                    })}
                                </Text>
                            </div>
                        ))}
                    </Stack>
                </ScrollArea>

                <Button
                    fullWidth
                    mt="lg"
                    size="md"
                    onClick={() => setNewCampaignOpened(true)}
                    className="bg-teal-600 hover:bg-teal-700"
                >
                    + Nova kampanja
                </Button>

                <CreateCampaignModal
                    opened={newCampaignOpened}
                    onClose={() => setNewCampaignOpened(false)}
                />
            </AppShell.Navbar>

            <AppShell.Main>
                {selectedCampaign ? (
                    <>
                        <div className="flex flex-col justify-center items-center p-5">
                            <Title order={6}>KAMPANJA</Title>
                            <br/>
                            <Title order={1}>" {selectedCampaign.name} "</Title>
                        </div>
                        <Tabs variant="outline" defaultValue="overview">
                            <Tabs.List>
                                <Tabs.Tab value="overview">Statistika</Tabs.Tab>
                                <Tabs.Tab value="rewards">Nagrade</Tabs.Tab>
                                <Tabs.Tab value="winners">Pobednici</Tabs.Tab>
                                <Tabs.Tab value="participants">Ucesnici</Tabs.Tab>
                                <Tabs.Tab value="settings">Podesavanja</Tabs.Tab>
                            </Tabs.List>

                            <Tabs.Panel value="overview" pt="xl">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

                                    {/* Big Participant Counter – More Distinguished */}
                                    <div className="col-span-1 md:col-span-2 lg:col-span-2 row-span-2">
                                        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-600 via-emerald-600 to-green-700 p-12 text-white shadow-2xl h-full flex items-center justify-center">
                                            <div className="text-center z-10 text-black">
                                                <div className="text-2xl md:text-3xl font-semibold mb-4">
                                                    Ukupno prijava
                                                </div>
                                                <div className="text-8xl md:text-9xl font-bold tracking-tight drop-shadow-lg">
                                                    {selectedCampaign.participants}
                                                </div>
                                            </div>
                                            <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-32 -mt-32"></div>
                                            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-10 rounded-full -ml-24 -mb-24"></div>
                                        </div>
                                    </div>

                                    {/* Participants Today Card */}
                                    <div className="col-span-1 md:col-span-2 lg:col-span-2">
                                        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 p-8 text-white shadow-xl h-full">
                                            <div className="text-center">
                                                <div className="text-lg md:text-xl font-medium mb-2 text-white opacity-95">
                                                    Prijave danas
                                                </div>
                                                <div className="text-6xl md:text-7xl font-bold tracking-tight text-white">
                                                    {selectedCampaign.participantsToday}
                                                </div>
                                            </div>
                                            <div className="absolute -bottom-6 -right-6 opacity-20">
                                                <div className="w-32 h-32 bg-white rounded-full"></div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Status Card */}
                                    <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
                                        <div className="flex items-center gap-4">
                                            <div className={`w-4 h-4 rounded-full ${selectedCampaign.status === 'active' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                            <div>
                                                <Text size="sm" c="dimmed" fw={500}>Status kampanje</Text>
                                                <Text size="xl" fw={700}>
                                                    {selectedCampaign.status === 'active' ? 'Aktivna' : 'Završena'}
                                                </Text>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Dates Card */}
                                    <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
                                        <Stack gap="md">
                                            <div>
                                                <Text size="sm" c="dimmed" fw={500}>Početak kampanje</Text>
                                                <Text size="lg" fw={600}>
                                                    {selectedCampaign.startDate
                                                        ? new Date(selectedCampaign.startDate).toLocaleDateString('sr-RS', {
                                                            day: '2-digit',
                                                            month: 'long',
                                                            year: 'numeric'
                                                        })
                                                        : 'Nije definisano'}
                                                </Text>
                                            </div>
                                            <Divider />
                                            <div>
                                                <Text size="sm" c="dimmed" fw={500}>Kraj kampanje</Text>
                                                <Text size="lg" fw={600} c={selectedCampaign.status === 'active' ? 'teal' : 'red'}>
                                                    {new Date(selectedCampaign.endDate).toLocaleDateString('sr-RS', {
                                                        day: '2-digit',
                                                        month: 'long',
                                                        year: 'numeric'
                                                    })}
                                                </Text>
                                            </div>
                                        </Stack>
                                    </div>
                                </div>
                            </Tabs.Panel>

                            <Tabs.Panel value="rewards">
                                <AdminRewardsTab campaignPid={selectedCampaign.pid}/>
                            </Tabs.Panel>
                            <Tabs.Panel value="winners">
                                <AdminWinnersTab campaignPid={selectedCampaign.pid}/>
                            </Tabs.Panel>
                            <Tabs.Panel value="participants">
                                <AdminParticipantsTab campaignPid={selectedCampaign.pid}/>
                            </Tabs.Panel>
                            <Tabs.Panel value="settings">
                                U razvoju...
                                {/*<CampaignSettingsForm/>*/}
                            </Tabs.Panel>
                        </Tabs>
                    </>
                ) : (
                    <div>
                        <Text>Izaberite kampanju da biste videli detalje.</Text>
                        <Skeleton height={25} mt={6} width="70%" radius="xl"/>
                        <Skeleton height={25} mt={6} width="70%" radius="xl"/>
                        <Skeleton height={25} mt={6} width="70%" radius="xl"/>
                        <Skeleton height={25} mt={6} width="70%" radius="xl"/>
                    </div>
                )}
            </AppShell.Main>
        </AppShell>
    );
}
